/**
 * AnchorRevealGate — Plan 10-05 wiring (D-11 / VIS-04), extended by Plan 14-13.
 *
 * The post-onboarding trigger for the ceremonial candidate reveal. Mounted
 * inside AppLayout (so it lives under ProtectedRoute + the single
 * ReverbStatusProvider that CandidateReveal's useReverbChannel needs) and
 * covers the whole /app surface as a full-screen overlay whenever the user
 * has a ready+unselected Anchor candidate set.
 *
 * Data source: GET /api/visions/pending returns the newest Anchor set's
 * STORED candidates (signed URLs) plus the total expected count. It is empty
 * once the user confirms a primary (users.active_vision_id set), so the
 * overlay disappears after selection and never shows for an existing user.
 *
 * The reveal is prop-driven and does not fetch itself (10-05); this gate is
 * the parent the plan always assumed but never shipped.
 *
 * ── SELF-08: why the absence has its own component ───────────────────────
 *
 * The same payload also carries `blocked_reason`, which says WHY there is no
 * anchor. There are four causes and they call for four different sentences; a
 * bare empty state cannot tell a user who has uploaded nothing apart from one
 * whose photos are still being checked.
 *
 * `AnchorBlockedNotice` renders those sentences, and it is deliberately NOT
 * rendered by this gate. The gate is a full-screen overlay, and an overlay is
 * the wrong shape for this: a user who declined photo consent would meet it on
 * every page forever, which is precisely the nagging the brand rule forbids.
 * The notice belongs where the anchor itself would have been, so Home
 * mounts it, in flow, once.
 *
 * No branch renders a symbolic or placeholder image. An absent anchor looks
 * absent — a stand-in would be the one thing worse than nothing here, because
 * the entire promise is that she is looking at herself.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '@/lib/api-client';
import { Button } from '@/components/ui/button';
import { useReverbChannel } from '@/hooks/useReverbChannel';
import { useVisionSelection } from '@/contexts/VisionSelectionContext';
import { CandidateReveal, type VisionCandidate } from './CandidateReveal';

/**
 * The complete vocabulary, owned by ReferenceSetResolver and
 * OnboardingSynthesisService and passed through by VisionGalleryController.
 */
const BLOCKED_REASONS = [
  'no_reference_photos',
  'reference_photos_unverified',
  'consent_reaffirmation_due',
  'prompt_guard_rejected',
] as const;

type BlockedReason = (typeof BLOCKED_REASONS)[number];

interface PendingReveal {
  candidate_set_id: string | null;
  expected_count: number;
  candidates: VisionCandidate[];
  /**
   * Every slot's media id, produced or not (Plan 14-14). The reveal correlates
   * broadcast frames against this, so a failure belonging to some other
   * generation cannot resolve one of these slots and a failure belonging to
   * this set no longer leaves a skeleton pending forever.
   */
  expected_media_ids: string[];
  blocked_reason: string | null;
}

function isBlockedReason(value: string): value is BlockedReason {
  return (BLOCKED_REASONS as readonly string[]).includes(value);
}

/**
 * Where each reason's one action leads. `Record<BlockedReason, …>` so a fifth
 * reason added upstream fails the build here instead of rendering a sentence
 * with no way out of it.
 *
 * `reference_photos_unverified` has `null` on purpose and it is the most
 * important entry in the table: the check finishes on its own and the anchor
 * resumes by itself (Plan 14-09), so there IS no action. Inventing a button
 * would be asking her to do work that does nothing.
 */
const ACTION_FOR: Record<BlockedReason, string | null> = {
  no_reference_photos: '/app/photos',
  reference_photos_unverified: null,
  consent_reaffirmation_due: '/app/photos',
  prompt_guard_rejected: '/app/onboarding',
};

function usePendingReveal() {
  const [pending, setPending] = useState<PendingReveal | null>(null);
  const [loaded, setLoaded] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const data = await api.get<PendingReveal>('/api/visions/pending');
      setPending(data);
    } catch {
      // A read failure must never trap the user behind a blank overlay — fall
      // back to the normal app shell.
      setPending(null);
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return { pending, loaded, refresh };
}

export function AnchorRevealGate() {
  const { pending, loaded, refresh } = usePendingReveal();
  const { notifyVisionSelected } = useVisionSelection();

  /**
   * Re-read when a generation lands (2026-08-16).
   *
   * `usePendingReveal` fetched once, on mount, and nothing ever asked it again.
   * `refresh` was passed down to CandidateReveal, but CandidateReveal is only
   * MOUNTED once `ready` is true, so the one component listening for
   * `vision.generation_succeeded` could not be listening on the one occasion
   * that mattered: the transition from no candidates to some.
   *
   * That went unnoticed because the reveal used to be reachable only by
   * finishing onboarding, which lands the user on a freshly mounted /app and
   * makes the mount fetch look like a live update. "Make my image" starts a
   * generation from a page the user is already sitting on, so the seam showed:
   * the images completed, the overlay never appeared, and a manual page refresh
   * was the only way to see them. No console error, because nothing failed.
   * Nothing was listening.
   *
   * Failure is deliberately not handled here. A failed set produces no
   * candidates, so there is nothing for this overlay to show, and the surfaces
   * that own the failure conversation say so in their own voice.
   */
  useReverbChannel({
    onVisionSucceeded: () => {
      void refresh();
    },
  });

  // Only surface the ceremonial reveal once at least one candidate image has
  // actually been produced; a set that is still generating leaves the app shell
  // visible rather than flashing a blank overlay.
  const ready =
    pending !== null && pending.candidate_set_id !== null && pending.candidates.length > 0;

  if (!loaded || !ready || pending === null || pending.candidate_set_id === null) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 z-50 overflow-auto bg-background"
      data-testid="anchor-reveal-overlay"
    >
      <CandidateReveal
        candidateSet={pending.candidate_set_id}
        initialCandidates={pending.candidates}
        expectedCount={pending.expected_count}
        expectedMediaIds={pending.expected_media_ids ?? []}
        // The gate owns fetching and the reveal owns rendering (10-05). A
        // broadcast frame says a candidate exists but carries an object key
        // rather than a signed URL, so the reveal asks for a re-read instead of
        // building a tile whose <img> could not load.
        onCandidateArrived={() => void refresh()}
        // Two things, and the second one is the whole point. `refresh` empties
        // this gate so the overlay closes; `notifyVisionSelected` tells the page
        // underneath that the world changed while it was covered up. Without it
        // the overlay lifts to reveal Home still asking for an image the user
        // has just chosen (operator, 2026-08-16).
        onConfirmed={() => {
          void refresh();
          notifyVisionSelected();
        }}
      />
    </div>
  );
}

/**
 * The honest empty state (SELF-08).
 *
 * Every sentence here is under D-v11-11 at its hardest: say what is not there
 * WITHOUT implying she failed to do something, and offer the one action that
 * changes it. `prompt_guard_rejected` is the only one of the four that is our
 * fault, and its copy says so rather than explaining prompt injection to her.
 *
 * Renders nothing at all when there is no reason to give. Silence is the
 * correct fallback, and it is also the common case.
 */
export function AnchorBlockedNotice() {
  const { t } = useTranslation();
  const { pending, loaded } = usePendingReveal();

  const reason = pending?.blocked_reason ?? null;

  if (!loaded || reason === null || !isBlockedReason(reason)) {
    return null;
  }

  const href = ACTION_FOR[reason];

  return (
    <div
      className="space-y-3 rounded-[0.75rem] border border-border bg-muted/40 p-4"
      data-testid={`anchor-blocked-${reason}`}
    >
      <p className="text-sm text-foreground">{t(`vision.blocked.${reason}`)}</p>
      {href !== null && (
        <Button asChild variant="default" size="sm">
          <Link to={href}>{t(`vision.blocked.${reason}_action`)}</Link>
        </Button>
      )}
    </div>
  );
}
